import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { configService } from './configService';
import { getApiUrl, rewriteNativeRequestUrl } from '../config/apiConfig';
import { emitSessionInvalid } from './sessionNavigation';
import { shouldInvalidateSessionOn401 } from '../utils/session401Guard';

const isProduction = process.env.NODE_ENV === 'production';

function logApiError(method: string, endpoint: string, error: unknown) {
  if (!isProduction) {
    console.error(`API ${method} Error (${endpoint}):`, error);
    return;
  }

  if (axios.isAxiosError(error)) {
    console.error(`API ${method} Error (${endpoint}):`, {
      status: error.response?.status,
      method: error.config?.method,
      url: error.config?.url,
      message: error.message,
    });
    return;
  }

  console.error(`API ${method} Error (${endpoint})`);
}

// Service API sécurisé qui détecte automatiquement la plateforme
class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = configService.getApiUrl();
    if (!isProduction) {
      console.log('[ApiService] Initializing with baseURL:', this.baseURL);
      console.log('[ApiService] Is Native:', configService.isNative());
      console.log('[ApiService] Is Mobile:', configService.isMobile());
    }
    this.api = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }

  private setupInterceptors(): void {
    // Intercepteur pour ajouter le token d'authentification ET corriger les URLs
    this.api.interceptors.request.use(
      config => {
        let url = config.url || '';
        const isNative = configService.isNative();

        // 1. Déterminer la bonne API (Principale ou Secondaire) pour cet endpoint
        // On récupère la base URL brute (ex: .../api ou http://localhost:8000)
        let targetBaseUrl = getApiUrl(url).replace(/\/+$/, '');

        // 2. Nettoyage du chemin — on enlève "/api/" de l'endpoint si présent
        let cleanPath = url;
        if (cleanPath.startsWith('/api/')) {
          cleanPath = cleanPath.substring(4); // Garde le slash initial
        } else if (cleanPath === '/api') {
          cleanPath = '/';
        }

        // S'assurer que le chemin commence par un slash UNIQUEMENT si ce n'est pas déjà une URL absolue
        if (cleanPath && !cleanPath.startsWith('/') && !cleanPath.startsWith('http')) {
          cleanPath = '/' + cleanPath;
        }

        if (isNative) {
          url = rewriteNativeRequestUrl(url.startsWith('http') ? url : cleanPath || url);
        } else if (!url.startsWith('http')) {
          // Web - S'assurer que l'on utilise la baseURL dynamique (Primary vs Secondary)
          url = `${targetBaseUrl}${cleanPath}`;
        } else {
          // Si l'URL fournie commence DÉJÀ par http (comme http://localhost:8000/pro/62/sold-forfaits),
          // on DOIT la réécrire avec la bonne targetBaseUrl pour forcer le basculement Port 8000 -> 8001
          // On extrait le path de cette URL absolue
          try {
            const parsedUrl = new URL(url);
            // Re-tester avec getApiUrl mais cette fois avec le vrai endpoint (sans le http://host)
            const realEndpoint = parsedUrl.pathname + parsedUrl.search;
            const newTargetBaseUrl = getApiUrl(realEndpoint).replace(/\/+$/, '');
            url = `${newTargetBaseUrl}${realEndpoint}`;
          } catch (e) {
            // Si ça fail on laisse comme ça
          }
        }

        // On override l'URL finale et on désactive le baseURL d'axios pour cette requête
        // car on a déjà construit l'URL complète.
        config.url = url;
        config.baseURL = '';

        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        const nativeH = configService.getNativeClientHeaders();
        if (nativeH) {
          Object.assign(config.headers, nativeH);
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Intercepteur pour gérer les erreurs d'authentification
    this.api.interceptors.response.use(
      response => {
        if (configService.isNative()) {
          try {
            if (localStorage.getItem('token')) {
              localStorage.setItem('lastLoginTime', String(Date.now()));
            }
          } catch {
            /* ignore */
          }
        }
        return response;
      },
      error => {
        const msg = error?.message && typeof error.message === 'string' ? error.message : '';
        if (
          (msg && msg.includes("Unexpected token '<'")) ||
          (error instanceof SyntaxError && msg.includes('Unexpected token'))
        ) {
          return Promise.reject(
            new Error(
              "Le serveur a renvoyé du HTML au lieu de JSON. Vérifiez REACT_APP_API_BASE_URL / le proxy et que l'API est joignable."
            )
          );
        }
        // 403 = droits / ressource interdite : ne jamais traiter comme une session invalide
        if (error.response?.status === 403) {
          return Promise.reject(error);
        }
        // Ne pas logout au 1er 401 (Google Calendar expiré, probe, etc.).
        // Aligné sur l’intercepteur Axios d’App : grâce + seuil.
        if (shouldInvalidateSessionOn401(error)) {
          emitSessionInvalid();
        }
        return Promise.reject(error);
      }
    );
  }

  // Méthodes HTTP sécurisées
  public async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.api.get(endpoint, config);
      return response;
    } catch (error) {
      logApiError('GET', endpoint, error);
      throw error;
    }
  }

  public async post<T>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.api.post(endpoint, data, config);
      return response;
    } catch (error) {
      logApiError('POST', endpoint, error);
      throw error;
    }
  }

  public async put<T>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.api.put(endpoint, data, config);
      return response;
    } catch (error) {
      logApiError('PUT', endpoint, error);
      throw error;
    }
  }

  public async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.api.delete(endpoint, config);
      return response;
    } catch (error) {
      logApiError('DELETE', endpoint, error);
      throw error;
    }
  }

  // Méthodes utilitaires
  public getBaseURL(): string {
    return this.baseURL;
  }

  public isMobile(): boolean {
    return configService.isMobile();
  }

  public isNative(): boolean {
    return configService.isNative();
  }

  // Méthode pour construire des URLs complètes
  public buildUrl(endpoint: string): string {
    return `${this.baseURL}${endpoint}`;
  }

  // Méthode pour vérifier la connectivité
  public async checkConnectivity(): Promise<boolean> {
    try {
      await this.api.get('/health');
      return true;
    } catch {
      return false;
    }
  }
}

// Instance singleton
export const apiService = new ApiService();

// Export des méthodes pour compatibilité
export const {
  get,
  post,
  put,
  delete: deleteRequest,
  getBaseURL,
  isMobile,
  isNative,
  buildUrl,
  checkConnectivity,
} = apiService;
